import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser, UserRole } from "../../../../../services/UserContext";
import { useAlert } from "../../../../../services/AlertContext";
import useFetch from "../../../../../services/functions/useFetch";
import snowflake from "../../../../../assets/icons/loading-snowflake.svg";
import CustomAlert from "../../../../CustomAlert";
import FileUpload, { MediaType } from "./FileUpload";

interface AgeResponse {
  ages: SelectOption[];
}

interface SkillResponse {
  skills: SelectOption[];
}

interface IdResponse {
  id: number;
}

interface SelectOption {
  id: number;
  title: string;
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function AddContent() {
  const query = useQuery();
  const { role } = useUser();
  const { triggerAlert } = useAlert();
  const { error, isPending, fetchData } = useFetch();

  const navigate = useNavigate();

  const [age, setAge] = useState<string>(query.get("age") ?? "");
  const [ageOptions, setAgeOptions] = useState<SelectOption[]>([]);
  const [skill, setSkill] = useState<string>(query.get("skill") ?? "");
  const [skillOptions, setSkillOptions] = useState<SelectOption[]>([]);
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [sort, setSort] = useState<string>("0");

  const [imageList, setImageList] = useState<{ source: File | string }[]>([]);
  const [videoList, setVideoList] = useState<{ source: File | string }[]>([]);

  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    if (role !== UserRole.ADMIN && role !== UserRole.EDITOR) {
      setErrorMsg("Keine Zugriffsberechtigung!");
    }
  }, [role]);

  useEffect(() => {
    fetchData(`/get/ages/`, "GET", {
      doOnSuccess: (response) => {
        const typedResponse = response as AgeResponse;
        setAgeOptions(typedResponse.ages);
      },
      doOnError: () => {
        console.error("Failed to fetch.");
      },
    });

    fetchData(`/get/skills/`, "GET", {
      doOnSuccess: (response) => {
        const typedResponse = response as SkillResponse;
        setSkillOptions(typedResponse.skills);
      },
      doOnError: () => {
        console.error("Failed to fetch.");
      },
    });
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!age || !skill || !title || !description) {
      setErrorMsg("Es müssen alle Felder ausgefüllt werden");
      return;
    }

    // Prepare the data payload
    const payload = {
      age_id: Number(age),
      skill_id: Number(skill),
      title: title,
      description: description,
      sort: Number(sort),
    };

    fetchData(`/add/exercise`, "POST", {
      body: payload,
      doOnSuccess: async (response) => {
        const addReponse = response as IdResponse;
        setErrorMsg("");
        await uploadFiles(imageList, addReponse.id, "pic");
        await uploadFiles(videoList, addReponse.id, "vid");

        triggerAlert("Übung erfolgreich erstellt", "success", 4000);
      },
      doOnError: () => {
        setErrorMsg("Erstellung fehlgeschlagen. Bitte erneut versuchen.");
        console.error("Failed to create exercise.");
      },
    });
  };

  const uploadFiles = async (
    fileList: { source: File | string }[],
    exerciseId: number,
    type: "pic" | "vid"
  ) => {
    try {
      for (const [index, file] of fileList.entries()) {
        const formData = new FormData();
        formData.append("file", file.source); // Append one file at a time

        const response = await fetchData<IdResponse>(
          `/upload/${type}/?exercise_id=${exerciseId}`,
          "POST",
          {
            body: formData,
          }
        );

        if (index === 0 && type === "pic") {
          await setThumbnail(exerciseId, response.id);
        }
      }
      setErrorMsg("");
    } catch (error) {
      console.error(error);
      setErrorMsg(`Upload fehlgeschlagen: ${error}`);
    }
  };

  const setThumbnail = async (exercise_id: number, thumbnail: number) => {
    const payload = {
      id: Number(exercise_id),
      age_id: Number(age),
      skill_id: Number(skill),
      thumbnail: Number(thumbnail),
      title: title,
      description: description,
      sort: Number(sort),
    };

    try {
      fetchData(`/update/exercise`, "POST", {
        body: payload,
        doOnSuccess: () => {
          setErrorMsg("");
          //console.log("Thumbnail set successfully.");

          navigate(-1);
        },
        doOnError: () => {
          setErrorMsg("Setting thumbnail failed. Please try again.");
          console.error("Failed to set thumbnail.");
        },
      });
    } catch (error) {
      console.error("Failed to update thumbnail:", error);
      setErrorMsg("Failed to update thumbnail. Please try again.");
    }
  };

  return (
    <>
      {error && <CustomAlert message={error} type="error" />}
      {isPending ? (
        <img src={snowflake} alt="loading" className="loading-snowflake" />
      ) : (
        <section className="section-container">
          <div className="content-wrapper">
            <div className="form-container">
              <h1 className="form-title">Übung erstellen</h1>
              <form className="form" onSubmit={handleSubmit} noValidate>
                {errorMsg && <p className="form-error">{errorMsg}</p>}
                {role === UserRole.ADMIN || role === UserRole.EDITOR ? (
                  <>
                    <div className="form-group">
                      <label htmlFor="age" className="form-label">
                        Altersklasse
                      </label>
                      <select
                        id="age"
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                        className="form-input"
                        required
                      >
                        <option
                          key="0"
                          value=""
                          disabled
                          style={{ color: "#dedbd5" }}
                        >
                          Altersklasse wählen:
                        </option>
                        {ageOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="skill" className="form-label">
                        Skillklasse
                      </label>
                      <select
                        id="skill"
                        value={skill}
                        onChange={(e) => setSkill(e.target.value)}
                        className="form-input"
                        required
                      >
                        <option
                          key="0"
                          value=""
                          disabled
                          style={{ color: "#dedbd5" }}
                        >
                          Skillklasse wählen:
                        </option>
                        {skillOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="password" className="form-label">
                        Titel
                      </label>
                      <input
                        type="text"
                        id="title"
                        autoComplete="off"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="description" className="form-label">
                        Beschreibung
                      </label>
                      <textarea
                        id="description"
                        autoComplete="off"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="form-area"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="sort" className="form-label">
                        Sortierrangfolge (klein=oben | groß=unten; 3-stellige
                        Eingabe möglich)
                      </label>
                      <input
                        type="number"
                        id="sort"
                        autoComplete="off"
                        value={sort}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 3 && /^\d*$/.test(value)) {
                            setSort(value);
                          }
                        }}
                        className="form-input"
                        min="0"
                        max="999"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="image" className="form-label">
                        Bilder auswählen
                      </label>
                      <FileUpload
                        accept=".png,.jpg"
                        idName="imageList"
                        mediaType={MediaType.Image}
                        infoText="Bilder können per Drag & Drop in der Reihenfolge verändert werden!<br />
                                  → Das erste Bild wird automatisch als Hauptbild verwendet."
                        onFilesSelected={setImageList}
                        files={imageList}
                        setFiles={setImageList}
                      />
                    </div>

                    <div
                      className="form-group"
                      style={{ paddingBottom: ".5rem" }}
                    >
                      <label htmlFor="video" className="form-label">
                        Videos auswählen
                      </label>
                      <FileUpload
                        accept=".mp4,.mkv,.m4p,.mov"
                        idName="videoList"
                        mediaType={MediaType.Video}
                        infoText="Videos können per Drag & Drop in der Reihenfolge verändert werden!"
                        onFilesSelected={setVideoList}
                        files={videoList}
                        setFiles={setVideoList}
                      />
                    </div>

                    <button type="submit" className="form-button">
                      Erstellen
                    </button>
                  </>
                ) : (
                  <p className="form-error">Keine Berechtigung</p>
                )}
              </form>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

export default AddContent;
